import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X, Loader2, SlidersHorizontal } from 'lucide-react';
import { clsx } from 'clsx';
import {
  RandEroare, RandRezultat, StareGoala, anuntRezultate, caleRezultat, definitieTip,
  etichetaFiltruTip, grupeazaRezultate, useCautare,
} from './SearchModal';
import { CAUTARE_MIN_CARACTERE } from '@/services/search.service';
import type { FiltruTipRezultat, RezultatCautare } from '@/types';

// ============================================================================
//  Pagina de rezultate — „vreau să văd tot".
//  Ruta: /cautare?q=…&tip=…&proiect=…
//
//  Filtrele stau în URL, nu în stare locală, ca rezultatul să fie PARTAJABIL:
//  într-o instituție, „uite, aici e" e jumătate din utilitatea unei căutări.
//
//  Filtrele se trimit BACKENDULUI, nu se aplică peste lista primită. Motivul e
//  măsurat, nu estetic: în interogare filtrele intră înaintea tăierii de zgomot
//  (`scor_maxim`), iar q='2026-245' cu tip=contract dă 2 rânduri de la server și
//  0 dacă filtrezi local aceeași listă nefiltrată.
//  Opțiunile de filtrare vin totuși din răspunsul NEFILTRAT al aceleiași
//  interogări — altfel, alegând un filtru, ai pierde toate celelalte opțiuni.
// ============================================================================

const INTARZIERE_URL_MS = 400;

interface OptiuneFiltru<T> {
  valoare: T;
  eticheta: string;
  numar: number;
}

export function CautarePage() {
  const navigate = useNavigate();
  const [parametriUrl, setParametriUrl] = useSearchParams();

  const qUrl = parametriUrl.get('q') ?? '';
  const tipUrl = (parametriUrl.get('tip') || null) as FiltruTipRezultat | null;
  const proiectUrl = Number(parametriUrl.get('proiect')) || null;

  const [text, setText] = useState(qUrl);
  const [indexSelectat, setIndexSelectat] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const randuriRef = useRef(new Map<string, HTMLElement>());

  // Intrare din afară (overlay-ul, un link partajat, butonul „înapoi"):
  // câmpul urmează URL-ul.
  useEffect(() => {
    setText((actual) => (actual.trim() === qUrl.trim() ? actual : qUrl));
  }, [qUrl]);

  // Ieșire spre URL: se scrie cu `replace`, ca istoricul să nu se umple cu o
  // intrare per literă tastată.
  useEffect(() => {
    if (text.trim() === qUrl.trim()) return;
    const ceas = setTimeout(() => {
      const noi = new URLSearchParams(parametriUrl);
      if (text.trim()) noi.set('q', text.trim());
      else noi.delete('q');
      setParametriUrl(noi, { replace: true });
    }, INTARZIERE_URL_MS);
    return () => clearTimeout(ceas);
  }, [text, qUrl, parametriUrl, setParametriUrl]);

  const areFiltre = !!tipUrl || !!proiectUrl;

  // Două căutări, nu una: cea nefiltrată dă opțiunile de filtrare (și numerele
  // de pe ele), cea filtrată dă lista afișată. Când nu există filtre, a doua
  // nici nu pornește (`activ: false`).
  const baza = useCautare(text, {});
  const filtrat = useCautare(text, { tip: tipUrl, proiect: proiectUrl, activ: areFiltre });

  const afisat = areFiltre ? filtrat : baza;
  const raspuns = afisat.raspuns;
  const seIncarca = afisat.seIncarca || (areFiltre && baza.seIncarca && !baza.raspuns);
  const eroare = afisat.eroare ?? (areFiltre ? baza.eroare : null);

  const grupuri = useMemo(() => grupeazaRezultate(raspuns?.rezultate ?? []), [raspuns]);

  // Opțiunile de filtru se calculează DOAR din rezultatele curente: un filtru
  // care ar da zero rezultate nu se afișează niciodată (decizia 9).
  const optiuniTip = useMemo<Array<OptiuneFiltru<FiltruTipRezultat>>>(() => {
    const numarare = new Map<string, number>();
    for (const rezultat of baza.raspuns?.rezultate ?? []) {
      numarare.set(rezultat.tip, (numarare.get(rezultat.tip) ?? 0) + 1);
    }
    const optiuni: Array<OptiuneFiltru<FiltruTipRezultat>> = Array.from(numarare.entries())
      .map(([tip, numar]) => ({
        valoare: tip as FiltruTipRezultat,
        eticheta: definitieTip(tip).eticheta,
        numar,
      }))
      .sort((a, b) => b.numar - a.numar);

    // Meta-valoarea acceptată de endpoint: toate cele cinci tipuri `document_*`
    // deodată. Are rost doar când chiar sunt mai multe feluri de document.
    const tipuriDeDocument = optiuni.filter((o) => o.valoare.startsWith('document_'));
    if (tipuriDeDocument.length > 1) {
      optiuni.unshift({
        valoare: 'document',
        eticheta: etichetaFiltruTip('document'),
        numar: tipuriDeDocument.reduce((s, o) => s + o.numar, 0),
      });
    }
    // Filtrul ales rămâne în listă chiar dacă lista nefiltrată s-a schimbat
    // între timp — altfel butonul apăsat ar dispărea de sub deget.
    if (tipUrl && !optiuni.some((o) => o.valoare === tipUrl)) {
      optiuni.unshift({ valoare: tipUrl, eticheta: etichetaFiltruTip(tipUrl), numar: 0 });
    }
    return optiuni;
  }, [baza.raspuns, tipUrl]);

  const optiuniProiect = useMemo<Array<OptiuneFiltru<number>>>(() => {
    const numarare = new Map<number, { nume: string; numar: number }>();
    for (const rezultat of baza.raspuns?.rezultate ?? []) {
      const proiect = rezultat.proiect;
      if (!proiect) continue;
      const existent = numarare.get(proiect.id);
      if (existent) existent.numar += 1;
      else numarare.set(proiect.id, { nume: proiect.nume, numar: 1 });
    }
    const optiuni = Array.from(numarare.entries())
      .map(([id, x]) => ({ valoare: id, eticheta: x.nume, numar: x.numar }))
      .sort((a, b) => b.numar - a.numar);
    if (proiectUrl && !optiuni.some((o) => o.valoare === proiectUrl)) {
      const numeCunoscut = raspuns?.rezultate?.find((r) => r.proiect?.id === proiectUrl)?.proiect?.nume;
      optiuni.unshift({ valoare: proiectUrl, eticheta: numeCunoscut ?? `Proiect #${proiectUrl}`, numar: 0 });
    }
    return optiuni;
  }, [baza.raspuns, proiectUrl, raspuns]);

  const seteazaFiltru = useCallback(
    (cheie: 'tip' | 'proiect', valoare: string | null) => {
      const noi = new URLSearchParams(parametriUrl);
      if (valoare) noi.set(cheie, valoare);
      else noi.delete(cheie);
      setParametriUrl(noi, { replace: false });
      setIndexSelectat(-1);
    },
    [parametriUrl, setParametriUrl]
  );

  // Indexul de tastatură urmează ordinea de randare (grupuri în ordinea primei
  // apariții, rânduri în ordinea scorului).
  const randuri = useMemo<RezultatCautare[]>(
    () => grupuri.flatMap((grup) => grup.rezultate),
    [grupuri]
  );

  useEffect(() => {
    setIndexSelectat(-1);
  }, [raspuns]);

  useEffect(() => {
    const rezultat = randuri[indexSelectat];
    if (!rezultat) return;
    randuriRef.current.get(rezultat.cheie)?.scrollIntoView({ block: 'nearest' });
  }, [indexSelectat, randuri]);

  const deschide = useCallback(
    (rezultat: RezultatCautare) => navigate(caleRezultat(rezultat)),
    [navigate]
  );

  const laTasta = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setIndexSelectat((i) => (randuri.length === 0 ? -1 : (i + 1) % randuri.length));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setIndexSelectat((i) => (randuri.length === 0 ? -1 : (i - 1 + randuri.length) % randuri.length));
          break;
        case 'Enter': {
          e.preventDefault();
          const rezultat = randuri[indexSelectat];
          if (rezultat) deschide(rezultat);
          break;
        }
        case 'Escape':
          // În pagină, Escape golește câmpul; nu navighează nicăieri (nu e dialog).
          e.preventDefault();
          setText('');
          break;
        default:
          break;
      }
    },
    [randuri, indexSelectat, deschide]
  );

  const q = text.trim();
  const total = raspuns?.total ?? 0;
  const trunchiat = raspuns?.trunchiat === true;
  const selectat = randuri[indexSelectat];
  const arataStareaGoala = !seIncarca && !eroare && (q.length < CAUTARE_MIN_CARACTERE || total === 0);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Căutare în dosarele investițiilor</h1>
        <p className="text-sm text-gray-500 mt-1">
          Se caută doar în proiectele la care aveți acces.
        </p>
      </div>

      {/* Câmpul */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3 px-4 py-3">
          <Search className="w-5 h-5 text-gray-400" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            autoFocus
            role="combobox"
            aria-expanded={randuri.length > 0}
            aria-controls="rezultate-cautare-pagina"
            aria-autocomplete="list"
            aria-activedescendant={selectat ? `pagina-${selectat.cheie}` : undefined}
            aria-label="Căutare în dosarele investițiilor"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={laTasta}
            placeholder="Număr de factură, contract, achiziție, document…"
            className="flex-1 bg-transparent text-gray-900 placeholder-gray-500 focus:outline-none"
          />
          {seIncarca ? (
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" aria-hidden="true" />
          ) : text ? (
            <button
              type="button"
              onClick={() => {
                setText('');
                inputRef.current?.focus();
              }}
              aria-label="Golește câmpul"
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          ) : null}
        </div>

        {/* Filtre — doar pe valorile care există în rezultatele curente */}
        {(optiuniTip.length > 1 || optiuniProiect.length > 1 || areFiltre) && (
          <div className="border-t border-gray-100 px-4 py-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <SlidersHorizontal className="w-4 h-4 text-gray-400" aria-hidden="true" />
              <span className="text-xs uppercase text-gray-500 mr-1">Tip</span>
              {optiuniTip.map((optiune) => (
                <ButonFiltru
                  key={optiune.valoare}
                  activ={tipUrl === optiune.valoare}
                  eticheta={optiune.eticheta}
                  numar={optiune.numar}
                  onClick={() =>
                    seteazaFiltru('tip', tipUrl === optiune.valoare ? null : optiune.valoare)
                  }
                />
              ))}
              {optiuniTip.length === 0 && <span className="text-xs text-gray-400">—</span>}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase text-gray-500 mr-1 ml-6">Proiect</span>
              {optiuniProiect.map((optiune) => (
                <ButonFiltru
                  key={optiune.valoare}
                  activ={proiectUrl === optiune.valoare}
                  eticheta={optiune.eticheta}
                  numar={optiune.numar}
                  onClick={() =>
                    seteazaFiltru(
                      'proiect',
                      proiectUrl === optiune.valoare ? null : String(optiune.valoare)
                    )
                  }
                />
              ))}
              {optiuniProiect.length === 0 && <span className="text-xs text-gray-400">—</span>}
            </div>

            {areFiltre && (
              <button
                type="button"
                onClick={() => {
                  const noi = new URLSearchParams(parametriUrl);
                  noi.delete('tip');
                  noi.delete('proiect');
                  setParametriUrl(noi, { replace: false });
                }}
                className="text-xs text-primary-600 hover:underline ml-6"
              >
                Șterge filtrele
              </button>
            )}
          </div>
        )}
      </div>

      {eroare && (
        <div className="mt-4">
          <RandEroare mesaj={eroare} onReincercare={afisat.reincarca} />
        </div>
      )}

      {/* Numărul de rezultate. La `trunchiat` nu se spune niciodată „toate". */}
      {!eroare && q.length >= CAUTARE_MIN_CARACTERE && raspuns && total > 0 && (
        <p className="text-sm text-gray-500 mt-4">
          {trunchiat
            ? `Se arată primele ${total} rezultate (există și altele — restrângeți căutarea).`
            : `${total} ${total === 1 ? 'rezultat' : 'rezultate'}`}
          {areFiltre && ' cu filtrele aplicate'}
        </p>
      )}

      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {anuntRezultate(raspuns, seIncarca)}
      </div>

      {/* Rezultate grupate. Fără paginare și fără derulare infinită: la volumele
          reale, tot ce trece de filtrul de acces încape (decizia 9). */}
      <div className="mt-2 space-y-4">
        <div id="rezultate-cautare-pagina" role="listbox" aria-label="Rezultatele căutării">
          {grupuri.map((grup) => (
            <div
              key={grup.tip}
              role="group"
              aria-labelledby={`grup-pagina-${grup.tip}`}
              className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4"
            >
              <p
                id={`grup-pagina-${grup.tip}`}
                className="px-4 py-2 text-xs font-medium text-gray-500 uppercase border-b border-gray-100"
              >
                {grup.eticheta} ({grup.rezultate.length})
              </p>
              <div className="p-2">
                {grup.rezultate.map((rezultat) => {
                  const index = randuri.indexOf(rezultat);
                  return (
                    <RandRezultat
                      key={rezultat.cheie}
                      rezultat={rezultat}
                      selectat={index === indexSelectat}
                      idElement={`pagina-${rezultat.cheie}`}
                      refElement={(el) => {
                        if (el) randuriRef.current.set(rezultat.cheie, el);
                        else randuriRef.current.delete(rezultat.cheie);
                      }}
                      onIndicare={() => setIndexSelectat(index)}
                      onActivare={deschide}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {arataStareaGoala && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-6">
            <StareGoala raspuns={raspuns} interogare={text} />
            {areFiltre && total === 0 && (
              <p className="pb-6 text-sm text-gray-500">
                Filtrele sunt active. <button
                  type="button"
                  className="text-primary-600 hover:underline"
                  onClick={() => {
                    const noi = new URLSearchParams(parametriUrl);
                    noi.delete('tip');
                    noi.delete('proiect');
                    setParametriUrl(noi, { replace: false });
                  }}
                >
                  Încercați fără filtre
                </button>.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ButonFiltru({
  activ,
  eticheta,
  numar,
  onClick,
}: {
  activ: boolean;
  eticheta: string;
  numar: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activ}
      title={eticheta}
      className={clsx(
        'text-xs rounded-full px-3 py-1 border transition-colors max-w-[16rem] truncate',
        activ
          ? 'bg-primary-50 border-primary-200 text-primary-700'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      )}
    >
      {eticheta}
      {numar > 0 && <span className="ml-1 text-gray-400">{numar}</span>}
    </button>
  );
}

export default CautarePage;
